import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import {
  fetchAwardComparison,
  fetchAwardComparisonSnapshot,
} from "data/actions/awardComparisonActions";
import Spinner from "../../Spinner";
import SelectedDateContext from "./SelectedDateContext";
import Card from "components/ui/DeprecatedCard";
import Instructions from "./Instructions";
import Dropdowns from "./Dropdowns";
import Legend from "./Legend";
import Summary from "./Summary";
import { Awards } from "./Awards";
import CostOfAttendance from "./CostOfAttendance";
import Profile from "./Profile";
import "./AwardComparison.scss";

const propTypes = {
  dispatch: PropTypes.func.isRequired,
  awardComparison: PropTypes.object.isRequired,
  awardComparisonSnapshot: PropTypes.object,
};

const Gap = () => {
  return <div className="gap" />;
};

// https://wikihub.berkeley.edu/display/SIS/CalCentral+Compare+Award+Packages+Card
const AwardComparison = ({
  dispatch,
  awardComparison,
  awardComparisonSnapshot,
}) => {
  const { finaidYearId } = useParams();

  useEffect(() => {
    dispatch(fetchAwardComparison());
  }, []);

  const [currentAidYear, setCurrentAidYear] = useState(finaidYearId);
  const [asOfCurrentDate, setAsOfCurrentDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState("X");
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [awardsExpanded, setAwardsExpanded] = useState(false);
  const [costExpanded, setCostExpanded] = useState(false);
  const [profileExpanded, setProfileExpanded] = useState(false);

  const { aidYears = [], loaded, errored, message } = awardComparison;

  useEffect(() => {
    if (aidYears.length > 0) {
      const activityDatesList = aidYears.find(
        (ay) => ay.id === currentAidYear
      ).activityDates;
      /* We will use the max activity date (element [0]) as the "Current" date on the card */
      setAsOfCurrentDate(activityDatesList[0]);
    }
  }, [aidYears, currentAidYear]);

  useEffect(() => {
    if (selectedDate !== "X") {
      dispatch(fetchAwardComparisonSnapshot(currentAidYear, selectedDate));
    }
  }, [selectedDate]);

  const loading = !loaded;
  const error = errored
    ? {
        message:
          "There is a problem displaying your information. Please try again soon.",
      }
    : null;

  const onAidYearChange = (newAidYear) => {
    setCurrentAidYear(newAidYear);
    setSelectedDate("X");
    setAsOfCurrentDate(null);
  };

  const onDateChange = (newSelectedDate) => {
    setSelectedDate(newSelectedDate);
  };

  const onExpand = (action, value) => {
    action(!value);
  };

  const rawCurrentAidYearData = aidYears.find((ay) => ay.id === currentAidYear);

  const {
    aidYears: {
      [currentAidYear]: { [selectedDate]: rawAidYearSnapshot } = {},
    } = {},
  } = awardComparisonSnapshot;

  // Effective aidYearIds >= 2025 replace the label "Expected Family Contribution"
  // with "Student Aid Index"
  // https://jira-secure.berkeley.edu/browse/SISRP-74015
  const currentAidYearData =
    {
      ...rawCurrentAidYearData,
      currentComparisonData: {
        ...rawCurrentAidYearData?.currentComparisonData,
        profile: {
          items:
            rawCurrentAidYearData?.currentComparisonData?.profile?.items.map(
              transformEFC(currentAidYear)
            ),
        },
      },
    } || {};

  const aidYearSnapshot = rawAidYearSnapshot
    ? {
        ...rawAidYearSnapshot,
        profile: {
          items: rawAidYearSnapshot?.profile?.items.map(
            transformEFC(currentAidYear)
          ),
        },
      }
    : rawAidYearSnapshot;

  return (
    <Card
      className="AwardComparison"
      title="Award Comparison"
      loading={loading}
      error={error}
      data-testid="financial-aid-award-comparison-card"
    >
      {!loading && !error && asOfCurrentDate && (
        <SelectedDateContext.Provider value={{ selectedDate: selectedDate }}>
          <div className="AwardComparison">
            <Instructions message={message} />
            <div className="container">
              <Dropdowns
                aidYear={currentAidYear}
                aidYears={aidYears}
                onAidYearChange={onAidYearChange}
                selectedDate={selectedDate}
                onDateChange={onDateChange}
              />
              <Legend
                asOfCurrentDate={asOfCurrentDate}
                selectedDate={selectedDate}
              />
              {selectedDate === "X" ||
              (aidYearSnapshot && aidYearSnapshot.loaded) ? (
                <>
                  <Gap />
                  <Summary
                    expanded={summaryExpanded}
                    onExpand={onExpand}
                    setExpand={setSummaryExpanded}
                    aidYearData={currentAidYearData}
                    aidYearSnapshot={aidYearSnapshot}
                  />
                  <Gap />
                  <Awards
                    expanded={awardsExpanded}
                    onExpand={onExpand}
                    setExpand={setAwardsExpanded}
                    aidYearData={currentAidYearData}
                    aidYearSnapshot={aidYearSnapshot}
                  />
                  <Gap />
                  <CostOfAttendance
                    expanded={costExpanded}
                    onExpand={onExpand}
                    setExpand={setCostExpanded}
                    aidYearData={currentAidYearData}
                    aidYearSnapshot={aidYearSnapshot}
                  />
                  <Gap />
                  <Profile
                    expanded={profileExpanded}
                    onExpand={onExpand}
                    setExpand={setProfileExpanded}
                    aidYearData={currentAidYearData}
                    aidYearSnapshot={aidYearSnapshot}
                  />
                </>
              ) : (
                <Spinner />
              )}
            </div>
          </div>
        </SelectedDateContext.Provider>
      )}
    </Card>
  );
};

AwardComparison.displayName = "AwardComparison";
AwardComparison.propTypes = propTypes;

const mapStateToProps = ({
  awardComparison = {},
  awardComparisonSnapshot = {},
}) => {
  return {
    awardComparison,
    awardComparisonSnapshot,
  };
};

const transformEFC = (currentAidYear) => (item) => {
  if (currentAidYear >= 2025) {
    return {
      ...item,
      description: item.description
        .replace("EFC", "SAI")
        .replace("Expected Family Contribution", "Student Aid Index"),
    };
  }

  return item;
};

export default connect(mapStateToProps)(AwardComparison);
